import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed, withDefaults } from 'vue';
import BasePopup from "@/components-UI/BasePopup.vue";
import SvgRangeCalendar from "@/assets/icons/range-calendar.svg";
import BaseButtonField from "@/components-UI/BaseButtonField.vue";
import { formatDateInputPicker } from "@/utils/formatDates";
import SvgArrowLeft from '@/assets/icons/scroll-arr-prev.svg';
import SvgArrowRight from "@/assets/icons/scroll-arr-next.svg";

type MonthValue = string;


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseMonthPicker',
  props: {
    value: null,
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    label: null,
    placeholder: null,
    view: null,
    position: { default: 'bottom' },
    size: { default: 'large' },
    availableDates: null,
    hidePrependIcon: { type: Boolean },
    teleportPopupToBody: { type: Boolean, default: true },
    clearableType: null,
    smallSize: { type: Boolean }
  },
  emits: ['update:value'],
  setup(__props: any, { emit: emits }) {

const props = __props as { value?: MonthValue, clearable: boolean, disabled?: boolean, readonly?: boolean, label?: string, placeholder?: string, view?: 'border-bottom' | 'outlined' | 'empty', position: 'top' | 'right' | 'bottom' | 'left', size: 'small' | 'medium' | 'large', availableDates?: { start: Date | null, end: Date | null, days?: number[] }, hidePrependIcon?: boolean, teleportPopupToBody: boolean, clearableType?: string, smallSize?: boolean }




const isShowPopup = ref<boolean>(false);
const currentYear = ref<number>(new Date().getFullYear());
const selectedMonth = ref<number | null>(null);
const selectedYear = ref<number | null>(null);
const slideDirection = ref<'slide-left' | 'slide-right'>('slide-right');
const isYearPickerMode = ref<boolean>(false);

const months = computed<string[]>(() => {
    return [
        'Янв.', 'Февр.', 'Март', 'Апр.',
        'Май', 'Июнь', 'Июль', 'Авг.',
        'Сент.', 'Окт.', 'Нояб.', 'Дек.',
    ];
});

const getButtonFieldValue = computed<boolean>(() => {
    return !!props.value;
});

const getButtonViewValue = computed<string>(() => {
    if (!props.value) return '';
    const dateStr = props.value.length === 7 ? `${props.value}-01` : props.value;
    return formatDateInputPicker(dateStr, 'LLLL yyyy');
});

const handleClear = (): void => {
    isShowPopup.value = false;
    emits('update:value', '');
};

const handleMonthSelect = (monthIndex: number): void => {
    const monthValue = `${currentYear.value}-${String(monthIndex).padStart(2, '0')}`;
    const dateStr = `${monthValue}-01`;
    
    if (validateAvailableDates(dateStr)) {
        selectedMonth.value = monthIndex;
        selectedYear.value = currentYear.value;
        updateValue();
        isShowPopup.value = false;
    }
};

const handlePrevYear = (): void => {
    if (isYearPickerMode.value) return;
    slideDirection.value = 'slide-right';
    currentYear.value -= 1;
};

const handleNextYear = (): void => {
    if (isYearPickerMode.value) return;
    slideDirection.value = 'slide-left';
    currentYear.value += 1;
};

const toggleYearPicker = (): void => {
    isYearPickerMode.value = !isYearPickerMode.value;
};

const handleYearSelect = (year: number): void => {
    const oldYear = currentYear.value;
    currentYear.value = year;
    
    if (year > oldYear) {
        slideDirection.value = 'slide-left';
    } else {
        slideDirection.value = 'slide-right';
    }
    
    isYearPickerMode.value = false;
};

const yearsList = computed<number[]>(() => {
    const current = new Date().getFullYear();
    const startYear = current - 50;
    const endYear = current + 10;
    const years: number[] = [];
    
    for (let year = endYear; year >= startYear; year--) {
        years.push(year);
    }
    
    return years;
});

const isMonthActive = (index: number): boolean => {
    if (selectedMonth.value === null || selectedYear.value === null) return false;
    return selectedMonth.value === index + 1 && selectedYear.value === currentYear.value;
};

const handleClose = (): void => {
    isShowPopup.value = false;
};

const handleClickOutside = (event: boolean): void => {
    isShowPopup.value = event;
    if (event && props.value) {
        const parts = props.value.match(/^(\d{4})-(\d{2})$/);
        if (parts) {
            currentYear.value = parseInt(parts[1]);
        }
    } else if (event && !props.value) {
        const now = new Date();
        currentYear.value = now.getFullYear();
    }
};


const initializeMonth = (value: MonthValue | undefined | null): void => {
    if (value == null || value === '') {
        selectedMonth.value = null;
        selectedYear.value = null;
        const now = new Date();
        currentYear.value = now.getFullYear();
    } else {
        const parts = value.match(/^(\d{4})-(\d{2})$/);
        if (parts) {
            const year = parseInt(parts[1]);
            const month = parseInt(parts[2]);
            const dateStr = `${value}-01`;
            
            if (validateAvailableDates(dateStr)) {
                selectedMonth.value = month;
                selectedYear.value = year;
                currentYear.value = year;
            } else {
                const now = new Date();
                currentYear.value = now.getFullYear();
            }
        } else {
            const now = new Date();
            currentYear.value = now.getFullYear();
        }
    }
};

const updateValue = (): void => {
    if (selectedMonth.value === null || selectedYear.value === null) {
        emits('update:value', '');
        return;
    }

    const monthValue = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}`;
    emits('update:value', monthValue);
};

const validateAvailableDates = (value: string): boolean => {
    if (!value) return false;
    
    const newDate = new Date(value);

    if (props.availableDates) {
        let { start, end, days } = props.availableDates;

        if (days && days?.length) {
            return !!days.find((el: number) => el === newDate.getDate());
        }

        if (start) {
            start = new Date(start);
            start.setHours(0, 0, 0, 0);
        }

        if (end) {
            end = new Date(end);
            end.setHours(0, 0, 0, 0);
        }

        if (start && end) {
            return newDate >= start && newDate <= end;
        } else if (start) {
            return newDate >= start;
        } else if (end) {
            return newDate <= end;
        } else {
            return true;
        }
    }

    return true;
};

watch(() => props.value, () => initializeMonth(props.value), { immediate: true });

return { __sfc: true,props, emits, isShowPopup, currentYear, selectedMonth, selectedYear, slideDirection, isYearPickerMode, months, getButtonFieldValue, getButtonViewValue, handleClear, handleMonthSelect, handlePrevYear, handleNextYear, toggleYearPicker, handleYearSelect, yearsList, isMonthActive, handleClose, handleClickOutside, initializeMonth, updateValue, validateAvailableDates, BasePopup, SvgRangeCalendar, BaseButtonField, SvgArrowLeft, SvgArrowRight }
}

})